// Turns the raw order and parcel event log into a short list of milestones.
// Nothing is dropped: every recorded event stays inside its milestone and can
// be opened. Only the grouping and the wording are presentation.

// Order-level and payment status changes are recorded alongside whatever stage
// was under way, so after the first stage they join it rather than start one.
const JOINS_CURRENT_STAGE = new Set(['payment', 'placed']);

const MILESTONES = Object.freeze({
  placed: 'Order placed',
  payment: 'Payment',
  packing: 'Packed and labelled',
  dispatch: 'Rider assigned and pickup',
  delivery: 'Out for delivery',
  returned: 'Returned to the store',
  cancelled: 'Cancelled',
  other: 'Other activity',
});

// [milestone, plain wording, is this a problem worth marking]
const SHIPMENT_EVENTS = Object.freeze({
  SHIPMENT_PENDING: ['placed', 'Parcel record created'],
  PARCEL_LABEL_ISSUED: ['packing', 'Label generated'],
  PARCEL_LABEL_ATTACHED: ['packing', 'Label confirmed on the parcel'],
  PARCEL_LABEL_REPLACED: ['packing', 'Label replaced'],
  DISPATCH_READINESS_VALIDATED: ['packing', 'Passed its checks and sent to dispatch'],
  DISPATCH_READINESS_FAILED: ['packing', 'Did not pass its dispatch checks', true],
  DISPATCH_READINESS_REVOKED: ['packing', 'Taken back out of dispatch', true],
  DELIVERY_ASSIGNMENT_COMMITTED: ['dispatch', 'Rider assigned'],
  DELIVERY_RIDER_WITHDRAWN: ['dispatch', 'Rider withdrawn from this parcel', true],
  DELIVERY_RUN_PLANNED: ['dispatch', 'Trip planned'],
  DELIVERY_RUN_READY: ['dispatch', 'Trip ready to leave'],
  DELIVERY_PICKUP_JOURNEY_STARTED: ['dispatch', 'Rider on the way to the store'],
  DELIVERY_PICKUP_ARRIVED: ['dispatch', 'Rider arrived at the store'],
  DELIVERY_PICKUP_RELEASED: ['dispatch', 'Store released the parcels'],
  PICKUP_MANIFEST_CORRECTED: ['dispatch', 'Pickup list corrected'],
  DELIVERY_RUN_PICKUP_CONFIRMED: ['dispatch', 'Rider confirmed the pickup'],
  DELIVERY_PICKED_UP: ['dispatch', 'Picked up by the rider'],
  DELIVERY_CUSTODY_RECEIVED: ['dispatch', 'Rider took the parcel'],
  DELIVERY_RUN_STARTED: ['delivery', 'Trip started'],
  DELIVERY_STOP_ACTIVE: ['delivery', 'Rider heading to the customer'],
  DELIVERY_STOP_ARRIVED: ['delivery', 'Rider arrived at the customer'],
  DELIVERY_COMPLETED: ['delivery', 'Delivered'],
  DELIVERY_ATTEMPT_FAILED: ['delivery', 'Delivery attempt failed', true],
  DELIVERY_STOP_DISPOSITION_RECORDED: ['delivery', 'Store decided what happens after the failed attempt'],
  DELIVERY_STOP_RETRY_AUTHORIZED: ['delivery', 'Another attempt authorized'],
  DELIVERY_PARCEL_RETURNED_TO_BUSINESS: ['returned', 'Parcel received back at the store', true],
  DELIVERY_RETURN_DISPOSITION_DECIDED: ['returned', 'Store decided what happens to the returned parcel'],
  DELIVERY_RETURN_MANAGEMENT_DECIDED: ['returned', 'Manager decided what happens to the returned parcel'],
});

const STATUS_EVENTS = Object.freeze({
  'ORDER:PENDING': ['placed', 'Order received'],
  'ORDER:TO_SHIP': ['placed', 'Order confirmed for fulfillment'],
  'ORDER:COMPLETED': ['delivery', 'Order completed'],
  'ORDER:CANCELLED': ['cancelled', 'Order cancelled', true],
  'PAYMENT:PENDING': ['placed', 'Payment pending'],
  'PAYMENT:PAID': ['payment', 'Payment received'],
  'PAYMENT:FAILED': ['payment', 'Payment failed', true],
  'PAYMENT:REFUNDED': ['payment', 'Payment refunded'],
  'FULFILLMENT:PROCESSING': ['packing', 'Packing started'],
  'FULFILLMENT:PACKED': ['packing', 'Packing completed'],
  'FULFILLMENT:READY_FOR_DISPATCH': ['packing', 'Ready for dispatch'],
  'FULFILLMENT:CANCELLED': ['cancelled', 'Fulfillment cancelled', true],
  'SHIPMENT:PENDING': ['placed', 'Parcel waiting to be prepared'],
  'SHIPMENT:IN_TRANSIT': ['delivery', 'Parcel with the rider'],
  'SHIPMENT:OUT_FOR_DELIVERY': ['delivery', 'Out for delivery'],
  'SHIPMENT:DELIVERED': ['delivery', 'Parcel delivered'],
  'SHIPMENT:FAILED': ['delivery', 'Delivery failed', true],
  'SHIPMENT:CANCELLED': ['cancelled', 'Parcel cancelled', true],
});

function readable(code) {
  const words = String(code || '').replaceAll('_', ' ').toLowerCase().trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Activity recorded';
}

export function describeOrderEvent(event = {}) {
  const type = String(event.type || '').toUpperCase();
  const status = String(event.status || '').toUpperCase();
  const known = SHIPMENT_EVENTS[status] || STATUS_EVENTS[`${type}:${status}`];
  const [milestone, label, problem] = known || ['other', readable(status || type), false];
  return { milestone, label, problem: Boolean(problem), note: event.note || null, at: event.at || null };
}

// Consecutive events of one milestone become one group, so a parcel that goes
// out twice shows two delivery groups in the order they happened.
export function groupOrderHistory(timeline = []) {
  const groups = [];
  (Array.isArray(timeline) ? timeline : []).forEach((raw) => {
    const event = describeOrderEvent(raw);
    const last = groups[groups.length - 1];
    if (last && (last.milestone === event.milestone || JOINS_CURRENT_STAGE.has(event.milestone))) {
      last.events.push(event);
    } else {
      groups.push({ milestone: event.milestone, title: MILESTONES[event.milestone], events: [event] });
    }
  });
  return groups.map((group, index) => {
    const latest = group.events[group.events.length - 1];
    return {
      ...group,
      key: `${group.milestone}-${index}`,
      startedAt: group.events[0].at,
      endedAt: latest.at,
      summary: latest.label,
      problem: group.events.some((event) => event.problem),
    };
  });
}
